import { debounce } from '@/modules/debounce';
import { fetchSanApi } from '@/modules/fetch-san-api';
import { getUniqueNumber } from '@/modules/get-unique-number';
import { metricForQuote } from '@/modules/san-api-metric';

export type PricePoint = {
  datetime: string;
  value: number;
};

export type PriceSeriesArgs = {
  slug: string;
  quoteAsset: string;
  // SanAPI datetime arguments — ISO strings or its relative forms
  // ("utc_now-1h"), passed through verbatim.
  from: string;
  to: string;
  interval: string;
};

// Frontend hardcodes coinmarketcap as the price source for every price
// fetch. Backend's `market.source` is ignored; the liquid-pair-only policy
// means CMC covers every supported market.
const PRICE_SOURCE = 'coinmarketcap';

// One aliased getMetric price-series field of a SanAPI document — the ONE
// place the price source and the timeseries selector shape are written.
// Callers compose several into one `{…}` document; the alias must be a
// valid GraphQL name (no leading digit).
export function priceSeriesField(alias: string, args: PriceSeriesArgs): string {
  return `${alias}:getMetric(metric:"${metricForQuote(args.quoteAsset)}"){timeseriesData(selector:{slug:"${args.slug}",source:"${PRICE_SOURCE}"},from:"${args.from}",to:"${args.to}",interval:"${args.interval}"){datetime value}}`;
}

type Answer = { timeseriesData?: PricePoint[] | undefined } | null | undefined;

type Waiter = {
  field: string;
  resolve: (points: PricePoint[] | null) => void;
  reject: (err: unknown) => void;
};

const BATCH_WINDOW_MS = 600;
const FLUSH_AT = 100;

let pending: Record<string, Waiter> = {};

function flushBatch(): void {
  const entries = Object.entries(pending);
  pending = {};
  fetchSanApi<Record<string, Answer> | null>(
    `{${entries.map(([, w]) => w.field).join(',')}}`,
  )
    .then(({ data }) => {
      for (const [alias, w] of entries) {
        const points = data?.[alias]?.timeseriesData;
        w.resolve(Array.isArray(points) ? points : null);
      }
    })
    .catch((err: unknown) => {
      for (const [, w] of entries) w.reject(err);
    });
}
const scheduleFlush = debounce(flushBatch, BATCH_WINDOW_MS);

// The shared SanAPI price-series batcher: series requested within one
// 600 ms window ride one document, each under its own alias; the 100th
// queued request flushes without waiting out the window. Resolves the
// alias's points, or null when SanAPI answers the alias with no series (an
// unknown slug comes back as a null field beside a top-level GraphQL
// error) — the caller decides what an unpriced series means. A failed
// request (network, non-JSON) rejects every waiter of its batch.
export function queuePriceSeries(
  args: PriceSeriesArgs,
): Promise<PricePoint[] | null> {
  const { promise, resolve, reject } = Promise.withResolvers<
    PricePoint[] | null
  >();
  const alias = `h${getUniqueNumber()}`;
  pending[alias] = { field: priceSeriesField(alias, args), resolve, reject };
  scheduleFlush();
  if (Object.keys(pending).length >= FLUSH_AT) scheduleFlush.flush();
  return promise;
}
